import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, FileText, Server, ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import api from "@/lib/api";

/**
 * Compliance — public-facing SOC 2 + HIPAA posture page.
 *
 * Everything on this page is truthful. If our SOC 2 audit is in progress
 * we say so plainly; we never claim attestation we don't have. Copy is
 * pulled from ``/api/compliance/status`` so a certified environment can
 * flip badges via env variables without a code change.
 */

const statusLabel = { in_progress: "Audit in progress", attested: "Attested", aligned: "HIPAA-aligned" };
const statusTone  = { in_progress: "#F59E0B", attested: "#10B981", aligned: "#22D3EE" };

export default function Compliance() {
  const [posture, setPosture] = useState(null);
  const [baa, setBaa] = useState({ name: "", email: "", organization: "", org_type: "", role: "", notes: "" });
  const [baaSent, setBaaSent] = useState(false);
  const [baaBusy, setBaaBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/compliance/status");
        setPosture(data);
      } catch { /* silent */ }
    })();
  }, []);

  const submitBAA = async (e) => {
    e.preventDefault();
    if (!baa.email || !baa.name || !baa.organization) {
      toast.error("Name, email and organization are required");
      return;
    }
    setBaaBusy(true);
    try {
      await api.post("/enterprise/baa", baa);
      setBaaSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (err?.message || "Could not send"));
    } finally {
      setBaaBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="compliance-page">
      <SiteNav />
      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
          Trust & Compliance
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
          Enterprise-grade security<br/>
          <span className="text-white/50">SOC 2 Type II · HIPAA-aligned · BAA-ready.</span>
        </h1>
        <p className="mt-6 text-white/70 max-w-2xl">
          BioHacker Sports&trade; handles athlete performance and health information —
          which means our security posture has to hold up to a medical-department review.
          We publish our controls, sub-processors, and data-handling posture publicly so
          your legal team never has to guess.
        </p>
      </section>

      {/* Badge row */}
      <section className="py-10 max-w-7xl mx-auto px-6" data-testid="compliance-badges">
        <div className="grid sm:grid-cols-3 gap-4">
          <Badge icon={Shield}
            label="SOC 2 Type II"
            state={posture?.soc2?.status}
            body="Independent audit covering security, availability, confidentiality, and processing integrity." />
          <Badge icon={Lock}
            label="HIPAA"
            state={posture?.hipaa?.status}
            body="Aligned with the HIPAA Security Rule + Privacy Rule. BAA available on request." />
          <Badge icon={Server}
            label="Data residency"
            state="attested"
            body={posture ? `Primary storage: ${posture.data.residency}. Encrypted at rest (AES-256) + in transit (TLS 1.2+).`
                          : "US-based storage, encrypted at rest + in transit."} />
        </div>
      </section>

      {/* Controls */}
      {posture && (
        <section className="py-10 border-t border-white/8 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6"
          data-testid="compliance-controls">
          <ControlBlock title="SOC 2 controls" items={posture.soc2.controls_in_place} tone="#F59E0B" testid="soc2-controls" />
          <ControlBlock title="HIPAA controls" items={posture.hipaa.controls_in_place} tone="#22D3EE" testid="hipaa-controls" />
        </section>
      )}

      {/* Sub-processors + data policy */}
      {posture && (
        <section className="py-10 border-t border-white/8 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="ink-card rounded-2xl p-6" data-testid="sub-processors">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3">Sub-processors</div>
            <ul className="space-y-2">
              {posture.sub_processors.map((sp) => (
                <li key={sp.name} className="flex justify-between text-sm text-white/85 border-b border-white/8 pb-2">
                  <span className="font-bold">{sp.name}</span>
                  <span className="text-white/60">{sp.purpose} · {sp.region}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="ink-card rounded-2xl p-6" data-testid="data-policy">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3">Data-handling policy</div>
            <ul className="text-sm text-white/85 space-y-2">
              <li><span className="text-white/50">Residency:</span> {posture.data.residency}</li>
              <li><span className="text-white/50">Retention:</span> {posture.data.retention_days_default} days default</li>
              <li><span className="text-white/50">Backups:</span> {posture.data.backup_frequency} · {posture.data.backup_retention_days} days</li>
              <li><span className="text-white/50">Encryption:</span> {posture.data.phi_encryption}</li>
            </ul>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mt-5 mb-2">Published policies</div>
            <div className="flex flex-wrap gap-1.5">
              {posture.policies.map((p) => (
                <span key={p} className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full border border-white/12 text-white/70">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BAA request */}
      <section className="py-14 border-t border-white/8 max-w-3xl mx-auto px-6" data-testid="baa-request">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Business Associate Agreement</div>
        <h2 className="font-display font-black text-3xl text-white tracking-tight">Request a BAA.</h2>
        <p className="mt-3 text-white/60">
          If your organization requires a Business Associate Agreement before onboarding, request one here.
          Our legal team returns a signed BAA within 3 business days.
        </p>
        {baaSent ? (
          <div className="mt-8 ink-card rounded-2xl p-6 flex items-start gap-3" data-testid="baa-success">
            <Check className="h-6 w-6 text-emerald-300 mt-0.5" />
            <div>
              <div className="font-display font-black text-lg text-white">BAA request received.</div>
              <div className="text-sm text-white/60 mt-1">Our legal team will follow up within 3 business days.</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submitBAA} className="mt-6 ink-card rounded-2xl p-6 space-y-4" data-testid="baa-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" value={baa.name} onChange={(v) => setBaa({ ...baa, name: v })} testid="baa-name" />
              <Field label="Work email" value={baa.email} onChange={(v) => setBaa({ ...baa, email: v })} testid="baa-email" type="email" />
              <Field label="Organization" value={baa.organization} onChange={(v) => setBaa({ ...baa, organization: v })} testid="baa-org" />
              <Field label="Org type (NCAA, hospital, etc.)" value={baa.org_type} onChange={(v) => setBaa({ ...baa, org_type: v })} testid="baa-orgtype" />
              <Field label="Your role" value={baa.role} onChange={(v) => setBaa({ ...baa, role: v })} testid="baa-role" />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">Notes</Label>
              <textarea rows={3} value={baa.notes} onChange={(e) => setBaa({ ...baa, notes: e.target.value })}
                className="mt-1 w-full rounded-md bg-white/[0.04] border border-white/12 text-white/90 text-sm p-3 focus:outline-none focus:border-white/30"
                data-testid="baa-notes" />
            </div>
            <Button type="submit" disabled={baaBusy}
              className="w-full text-white font-bold text-sm py-6"
              style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="baa-submit">
              {baaBusy
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Sending…</>
                : <>Request BAA <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}

function Badge({ icon: Icon, label, state, body }) {
  const key = state || "in_progress";
  return (
    <div className="ink-card rounded-2xl p-5" data-testid={`compliance-badge-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className="h-6 w-6" style={{ color: statusTone[key] }} />
        <div className="flex-1 font-display font-black text-white text-lg">{label}</div>
        <span className="text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-full border"
          style={{ borderColor: `${statusTone[key]}55`, color: statusTone[key] }}>
          {statusLabel[key] || "In progress"}
        </span>
      </div>
      <div className="text-sm text-white/65 leading-relaxed">{body}</div>
    </div>
  );
}

function ControlBlock({ title, items, tone, testid }) {
  return (
    <div className="ink-card rounded-2xl p-6" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3">{title}</div>
      <ul className="space-y-1.5">
        {items.map((c) => (
          <li key={c} className="flex items-start gap-2 text-sm text-white/85">
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone }} /> {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-white/70 text-xs uppercase tracking-[0.15em]">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 bg-white/[0.04] border-white/12 text-white/90" data-testid={testid} />
    </div>
  );
}
